import IconButton from '@/global/components/Button/IconButton'
import { isMac } from '@/lib/utils'
import { MoonIcon, SunIcon } from 'lucide-react'
import React, { useEffect, useId, useLayoutEffect } from 'react'
import { flushSync } from 'react-dom'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const ToggleTheme: React.FC = () => {
    const mode = useThemeStore(state => state.theme)
    const toggleTheme = useThemeStore(state => state.toggle)
    // Nome único por instância: nome repetido na página aborta a view transition.
    const iconeStyle = {
        viewTransitionName: `icone-tema-${useId().replace(/:/g, '')}`,
    }

    return (
        <IconButton
            size="small"
            color="inherit"
            onClick={toggleTheme}
            title={`Alternar tema (${isMac() ? '⌘' : 'CTRL'} + I)`}
            className="ml-1"
        >
            {mode === 'dark' ? (
                <SunIcon
                    className="text-warning size-6 [view-transition-class:icone-tema]"
                    style={iconeStyle}
                />
            ) : (
                <MoonIcon
                    className="text-card/80 size-6 [view-transition-class:icone-tema]"
                    style={iconeStyle}
                />
            )}
        </IconButton>
    )
}

/**
 * Aplica o tema na raiz do documento e alterna com Ctrl+I em qualquer tela,
 * inclusive nas que não exibem o {@link ToggleTheme}.
 */
export const ThemeSync: React.FC = () => {
    const mode = useThemeStore(state => state.theme)
    const toggleTheme = useThemeStore(state => state.toggle)

    // Layout effect: roda no commit síncrono do flushSync, dentro do snapshot novo.
    useLayoutEffect(() => {
        document.documentElement.classList.toggle('dark', mode === 'dark')
        document.documentElement.classList.toggle('light', mode !== 'dark')
    }, [mode])

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (
                e.key.toLowerCase() === 'i' &&
                (e.metaKey || e.ctrlKey) &&
                !e.shiftKey
            ) {
                e.preventDefault()
                toggleTheme()
            }
        }
        document.addEventListener('keydown', down)
        return () => document.removeEventListener('keydown', down)
    }, [toggleTheme])

    return null
}

/**
 * Executa `trocarTema` com crossfade da página e sem as transições dos componentes,
 * que senão seguiriam animando depois do crossfade.
 */
function animarTrocaTema(trocarTema: () => void) {
    const raiz = document.documentElement
    const liberarTransicoes = () => raiz.classList.remove('trocando-tema')
    raiz.classList.add('trocando-tema')

    if (!document.startViewTransition) {
        trocarTema()
        // Força o recálculo de estilo antes de liberar, senão a transição dispara mesmo assim.
        window.getComputedStyle(document.body).opacity
        return liberarTransicoes()
    }
    document.startViewTransition(trocarTema).finished.finally(liberarTransicoes)
}

export const useThemeStore = create(
    persist<{ theme: 'dark' | 'light'; toggle: () => void }>(
        (set, get) => ({
            theme: 'light',
            // flushSync: o DOM precisa mudar dentro do callback, senão o snapshot antigo já sai com o ícone novo.
            toggle: () =>
                animarTrocaTema(() =>
                    flushSync(() =>
                        set({
                            theme: get().theme === 'dark' ? 'light' : 'dark',
                        }),
                    ),
                ),
        }),
        {
            name: 'theme',
        },
    ),
)
